import { useRef, useState } from "react";
import { Play } from "lucide-react";

export const FARMACLASS_VIDEOS = [
  { id: "presentacion", src: "/farmaclass-presentacion.mp4", label: "Presentación", cta: "Ver la presentación" },
  { id: "que-hace", src: "/farmaclass-que-hace.mp4", label: "Qué hace FarmaClass", cta: "Ver qué hace FarmaClass", highlight: true },
];

/** Reproductor de los vídeos de FarmaClass (presentación + qué hace). */
export const PresentationVideo = ({
  videos = FARMACLASS_VIDEOS,
  className = "",
  size = "md",
  testid = "presentation-video",
}) => {
  const lg = size === "lg";
  const ref = useRef(null);
  const [idx, setIdx] = useState(0);
  const [started, setStarted] = useState(false);
  const active = videos[idx];

  const play = () => {
    setStarted(true);
    const v = ref.current;
    if (v) { v.muted = false; v.play().catch(() => { v.muted = true; v.play(); }); }
  };

  const select = (i) => {
    setIdx(i);
    setStarted(false);
    const v = ref.current;
    if (v) { v.pause(); v.load(); }
  };

  return (
    <div className={className} data-testid={testid}>
      <div className={`relative overflow-hidden bg-slate-950 ${lg ? "rounded-2xl shadow-2xl ring-1 ring-slate-900/10" : "rounded-xl shadow-xl"}`}>
        <video
          ref={ref}
          key={active.src}
          controls={started}
          playsInline
          preload="metadata"
          className="w-full h-full block aspect-video"
          data-testid={`${testid}-el`}
        >
          <source src={active.src} type="video/mp4" />
          Tu navegador no puede reproducir el vídeo.
        </video>

        {!started && (
          <button
            type="button"
            onClick={play}
            className="absolute inset-0 grid place-items-center bg-slate-950/55 hover:bg-slate-950/40 transition-colors group"
            data-testid={`${testid}-play`}
          >
            <span className="flex flex-col items-center gap-3">
              <span className={`rounded-full bg-emerald-500 text-slate-950 grid place-items-center shadow-xl transition-transform duration-200 group-hover:scale-110 ${lg ? "h-24 w-24" : "h-16 w-16"}`}>
                <Play className={lg ? "h-11 w-11 ml-1.5" : "h-7 w-7 ml-1"} strokeWidth={2.4} fill="currentColor" />
              </span>
              <span className={`text-white font-bold ${lg ? "text-lg md:text-xl" : "text-sm"}`}>{active.cta}</span>
            </span>
          </button>
        )}
      </div>

      {videos.length > 1 && (
        <div className={`flex flex-wrap justify-center ${lg ? "mt-6 gap-3" : "mt-3 gap-2"}`} data-testid={`${testid}-tabs`}>
          {videos.map((v, i) => (
            <button
              key={v.id}
              type="button"
              onClick={() => select(i)}
              className={`relative overflow-hidden inline-flex items-center gap-2 font-bold rounded-full border transition-[background-color,border-color,color,box-shadow] ${
                lg ? "text-base md:text-lg px-7 py-3.5" : "text-xs px-3 py-2"
              } ${
                i === idx
                  ? "bg-slate-950 text-white border-slate-950 shadow-lg"
                  : v.highlight
                    ? `lead-cta booking-glow bg-gradient-to-r from-emerald-500 to-teal-500 text-white border-emerald-400 font-extrabold ${lg ? "text-lg md:text-xl px-9 py-4" : ""}`
                    : "bg-white text-slate-700 border-slate-300 hover:border-slate-900 hover:text-slate-950"
              }`}
              data-testid={`${testid}-tab-${v.id}`}
            >
              {v.highlight && i !== idx && <span className="booking-shine pointer-events-none absolute inset-y-0 -left-1/2 w-1/2" aria-hidden="true" />}
              {v.highlight && <Play className={lg ? "h-5 w-5" : "h-3.5 w-3.5"} strokeWidth={2.4} fill="currentColor" />}
              <span className="relative">{v.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PresentationVideo;
